// 伺服器版面（plan/vms-web-v0.md LIVE-01、§7 vms.layouts）：個人版面與 org 共用版面，存、載入、覆寫、刪除。
// 共用版面逐格驗權：沒有權限的格子只回 denied（不帶設備），牆上顯示「無權限」；覆寫時原樣送回 denied，伺服器保留原格。
import { useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
// 存完版面後左欄「檢視」分頁也看得到（同一個 ['vms-layouts', api] 快取）
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { layoutApi, type LayoutCell, type ServerLayout } from '../../lib/deviceApi';
import { GRID_SIZES, wallOf, type GridSize, type Wall } from './wall';
import { parseTour, type TourSpec } from './tour';

/** 牆上格子的值：VmsDevice.key、null（空格）、或沒權限的格子。 */
export const DENIED = '!denied';

export function toServerCells(cells: (string | null)[]): (LayoutCell | null)[] {
  return cells.map((k) => {
    if (!k) return null;
    if (k === DENIED) return { denied: true };
    const i = k.indexOf('/');
    return { home_id: k.slice(0, i), device_ref: k.slice(i + 1) };
  });
}

export function fromServerCells(cells: (LayoutCell | null)[]): (string | null)[] {
  return cells.map((c) => (!c ? null : c.denied ? DENIED : c.home_id && c.device_ref ? `${c.home_id}/${c.device_ref}` : null));
}

export function gridOf(n: number): GridSize {
  return GRID_SIZES.find((g) => g >= n) ?? 36;
}

/** 伺服器版面（個人 + 組織共用）：「版面…」選單與左欄「檢視」分頁讀同一份（SPEC LIVE「檢視、版面、群組」，第二輪 R2-P1-8）。 */
export function useServerLayouts() {
  const { visible } = useScope();
  const apis = [...new Set(visible.map((h) => h.endpoints.api))];
  const lists = useQueries({ queries: apis.map((api) => ({ queryKey: ['vms-layouts', api], queryFn: () => layoutApi.list(api), retry: false })) });
  const layouts: ServerLayout[] = lists.flatMap((q) => q.data?.data ?? []);
  const adminOf = lists.flatMap((q, i) => (q.data?.orgAdminOf ?? []).map((org) => ({ org, api: apis[i] })));
  return { apis, layouts, adminOf, loading: lists.some((q) => q.isLoading) };
}

/** 版面 → 影像牆的格數與格子（載入版面用；LivePage 從左欄「檢視」帶 loadLayout 進來也用這個）。 */
export function layoutWall(l: ServerLayout): { wall: Wall; cells: (string | null)[]; tour: TourSpec | null } {
  return { wall: l.cols ? wallOf(l.grid, l.cols) : wallOf(gridOf(l.grid)), cells: fromServerCells(l.cells), tour: parseTour(l.tour) };
}

/** 打開左欄「檢視」分頁（收合時先展開）：版面的唯一入口（SPEC「檢視、版面、群組」一個入口，第三輪 R3-P1-9）。 */
export const OPEN_TREE_TAB = 'kinio-open-tree-tab';
export function openViewsTab(): void {
  window.dispatchEvent(new CustomEvent(OPEN_TREE_TAB, { detail: 'views' }));
}

/**
 * 頁首工具列的版面動作：〔另存版面〕〔管理版面…〕（打開左欄「檢視」分頁）；從「檢視」載入的版面另有〔儲存〕（覆寫）與 🗑。
 * 不再有「版面…」下拉——套用版面一律在左欄「檢視」分頁（SPEC LIVE「檢視、版面、群組」，第三輪 R3-P1-9）。
 */
export function LayoutMenu({
  wall,
  cells,
  tour,
  currentId,
  onCurrent,
}: {
  wall: Wall;
  cells: (string | null)[];
  /** 目前牆上的輪巡設定（存版面時一起存；LIVE-05） */
  tour?: TourSpec | null;
  /** 目前的牆是從哪個存過的版面載入的 */
  currentId?: string | null;
  onCurrent?(id: string | null): void;
}) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const { visible, orgs } = useScope();
  const { apis, layouts, adminOf, loading } = useServerLayouts();
  const found = currentId ? (layouts.find((x) => x.layout_id === currentId) ?? null) : null;
  const [own, setOwn] = useState<ServerLayout | null>(null);
  const cur = own && own.layout_id === currentId ? own : found;
  const setCur = (l: ServerLayout | null) => (setOwn(l), onCurrent?.(l?.layout_id ?? null));
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('user');
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => apis.forEach((api) => void qc.invalidateQueries({ queryKey: ['vms-layouts', api] }));
  // 存在哪個 plane：第一個有設備的格子所在家戶的 plane，否則第一個
  const apiFor = () => {
    const k = cells.find((c) => c && c !== DENIED);
    const h = k ? visible.find((x) => x.home_id === k.slice(0, k.indexOf('/'))) : undefined;
    return h?.endpoints.api ?? apis[0];
  };
  const fail = (e: unknown) => setErr((e as { code?: string }).code ?? String(e));

  const overwrite = async () => {
    if (!cur) return;
    setErr(null);
    try {
      const out = await layoutApi.update(cur.api, cur.layout_id, {
        expected_version: cur.version,
        name: cur.name,
        grid: wall.size,
        cols: wall.custom ? wall.cols : 0,
        tour: tour ?? null,
        cells: toServerCells(cells),
      });
      setCur({ ...out, api: cur.api });
      refresh();
    } catch (e) {
      fail(e);
    }
  };
  const create = async () => {
    setErr(null);
    const org = scope.startsWith('org:') ? adminOf.find((x) => x.org === scope.slice(4)) : undefined;
    const api = org?.api ?? apiFor();
    try {
      const out = await layoutApi.create(api, {
        scope: org ? 'org' : 'user',
        org_id: org?.org,
        name: name.trim(),
        grid: wall.size,
        cols: wall.custom ? wall.cols : 0,
        tour: tour ?? null,
        cells: toServerCells(cells).map((c) => (c?.denied ? null : c)),
      });
      setCur({ ...out, api });
      setSaving(false);
      setName('');
      refresh();
    } catch (e) {
      fail(e);
    }
  };
  const remove = async () => {
    if (!cur || !(await ask({ danger: true, title: t('layouts.delete_confirm', { name: cur.name }), action: t('common.action_delete') }))) return;
    try {
      await layoutApi.remove(cur.api, cur.layout_id, cur.version);
      setCur(null);
      refresh();
    } catch (e) {
      fail(e);
    }
  };
  const orgName = (id?: string) => orgs.find((o) => o.org_id === id)?.display_name ?? id ?? '';

  return (
    <>
      <button className="btn" onClick={openViewsTab} title={layouts.length === 0 && !loading ? t('layouts.none') : undefined} data-testid="layout-manage">
        {t('layouts.manage')}
      </button>
      {cur && <span className="chip small" data-testid="layout-current">{cur.scope === 'org' ? `🏢 ${orgName(cur.owner_org_id)} / ${cur.name}` : cur.name}</span>}
      {cur?.can_write && (
        <button className="btn" onClick={() => void overwrite()}>
          {t('layouts.save')}
        </button>
      )}
      <button className="btn" onClick={() => (setErr(null), setSaving(true))}>
        {t('layouts.save_as')}
      </button>
      {cur?.can_write && (
        <button className="btn" onClick={() => void remove()} aria-label={t('layouts.delete')}>
          🗑
        </button>
      )}
      {err && !saving && <span className="error small">{t(`layouts.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</span>}
      <Dialog open={saving} title={t('layouts.save_as')} onClose={() => setSaving(false)}>
        <label className="field">
          <span>{t('layouts.name')}</span>
          <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} aria-label={t('layouts.name')} />
        </label>
        <label className="field">
          <span>{t('layouts.scope')}</span>
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value)} aria-label={t('layouts.scope')}>
            <option value="user">{t('layouts.scope_user')}</option>
            {adminOf.map((x) => (
              <option key={x.org} value={`org:${x.org}`}>
                {t('layouts.scope_org', { org: orgName(x.org) })}
              </option>
            ))}
          </select>
        </label>
        {cells.includes(DENIED) && <p className="muted small">{t('layouts.denied_dropped')}</p>}
        {err && <p className="error">{t(`layouts.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={() => setSaving(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={!name.trim()} onClick={() => void create()}>
            {t('common.save')}
          </button>
        </div>
      </Dialog>
      {dialog}
    </>
  );
}
